import UserFormConfig from "./form";
import GenericForm from "../GenericForm";
import { UserApi, UserDataType } from "@/api/UserApi";
import { useMemo, useRef } from "react";
import { DynamicFormRef } from "@mavichpe/dynamic-forms";
import { UserFormData } from "@/formTypes/UserDataType";
import { ApiClient } from "@/api/ApiClient";
import { normalizeStoreId, normalizeTerminalId } from "@utils/EDocument";
import { refreshSessionIfCurrentUser } from "@utils/Auth";

type UserFormProps = {
  id?: string;
  formId: string,
  saveRedirectUrl: string
}

const UserForm = (props: UserFormProps) => {
  const formRef = useRef<DynamicFormRef>(null);

  /**
   * Normaliza sucursal y terminal al formato de Hacienda (3 y 5 dígitos) antes de
   * persistir, para que el usuario pueda escribir "2" y se guarde "002" / "00002".
   * Se usa string vacío en vez de null porque GenericModelApi.update() descarta las
   * propiedades nulas y no permitiría limpiar el valor.
   */
  const withNormalizedIds = (input: UserDataType): UserDataType => ({
    ...input,
    storeId: input.storeId ? normalizeStoreId(input.storeId) : "",
    terminalId: input.terminalId ? normalizeTerminalId(input.terminalId) : "",
  })

  /**
   * Cognito va primero, tanto al crear como al editar, porque es la fuente de verdad del
   * correo (el username es el email) y es quien rechaza los duplicados. Si se escribiera
   * antes el registro en DynamoDB, un correo repetido dejaria un usuario huerfano en la
   * lista sin cuenta para iniciar sesion, y un fallo de la lambda al editar dejaria las dos
   * fuentes divergentes (DynamoDB con el nombre nuevo y Cognito con el viejo).
   */
  const createUser = async (formData: UserDataType): Promise<UserDataType> => {
    const { password, ...rest } = formData
    const input = withNormalizedIds(rest)
    await createCognitoUser(input, `${password || ""}`)
    const createdUser = await UserApi.create(input);
    // La validacion de unicidad cachea la lista del tenant: se invalida al cambiarla.
    UserApi.clearEmailLookupCache();
    return createdUser;
  }

  const createCognitoUser = async (input: UserDataType, password: string) => {
    const saveUsers = await ApiClient.mutations.createTenantUserMutation({
      email: `${input.email}`,
      password: password,
      roleId: `${input.roleId}`,
      name: `${input.name}`,
      storeId: `${input.storeId || ""}`,
      terminalId: `${input.terminalId || ""}`,
      sendInvite: false,
      suppressEmail: true
    })
    if (!saveUsers.data?.ok) {
      // La lambda distingue el correo duplicado ("El usuario ya existe"): se muestra su
      // mensaje en vez de uno generico que no explica el problema.
      throw Error(saveUsers.data?.error || "No fue posible crear el usuario en el sistema")
    }
  }

  const updateCognitoUser = async (input: UserDataType, password: string) => {
    const saveUsers = await ApiClient.mutations.updateTenantUserMutation({
      username: `${input.email}`,
      email: `${input.email}`,
      password: password,
      roleId: `${input.roleId}`,
      name: `${input.name}`,
      storeId: `${input.storeId || ""}`,
      terminalId: `${input.terminalId || ""}`
    })
    if (!saveUsers.data?.ok) {
      throw Error(saveUsers.data?.error || "No fue posible actualizar el usuario en el sistema")
    }
  }

  const updateUser = async (id: string, formData: UserDataType): Promise<UserDataType> => {
    const { password, ...rest } = formData
    const input = withNormalizedIds(rest)
    await updateCognitoUser(input, `${password || ""}`)
    const saveUser = await UserApi.update(id, input)
    // La validacion de unicidad cachea la lista del tenant: se invalida al cambiarla.
    UserApi.clearEmailLookupCache();
    await refreshSessionIfCurrentUser(`${input.email}`)
    return saveUser
  }

  // Cognito no permite cambiar el username y este es el correo, asi que en edicion el campo
  // se bloquea: cambiarlo solo actualizaria el atributo y dejaria el login con el valor viejo.
  const unEditableFields = useMemo<Array<keyof UserFormData>>(
    () => (props.id ? ["email"] : []),
    [props.id]
  );

  return (
    <>
      <GenericForm<UserFormData, UserDataType>
        id={props.id}
        ref={formRef}
        formId={props.formId}
        unEditableFields={unEditableFields}
        saveRedirectUrl={props.saveRedirectUrl}
        formConfig={UserFormConfig}
        getById={(...arg) => UserApi.get(...arg)}
        createFn={(...arg) => createUser(...arg)}
        updateFn={(...arg) => updateUser(...arg)}
        successMessage="El usuario ha sido almacenada correctamente"
        errorMessage="No fue posible almacenar la información del usuario"
      />
    </>
  )
}

export default UserForm;